import { createElement } from 'preact'
import type { JSX } from 'preact'
import type { TextProps, TypographyProps } from '@/types/typography'
import {
  TEXT_VARIANT_MAP,
  TYPOGRAPHY_STYLES,
  COLOR_STYLES,
  DEFAULT_ELEMENTS,
} from '@/types/typography'

/**
 * Text component supporting both text variants and full typography variants
 * Can handle all typography use cases including headings, body text, and special text
 *
 * @param variant - Text size variant (large, base, small, extra-small) or full typography variant
 * @param typographyVariant - Full typography variant (heading-1, heading-2, body, etc.)
 * @param as - HTML element to render as
 * @param truncate - Whether to truncate text with ellipsis
 * @param color - Text color override
 * @param className - Additional CSS classes
 * @param children - Content to render
 */
export function Text({
  variant = 'base',
  typographyVariant,
  as,
  truncate = false,
  color,
  className = '',
  children,
  ...props
}: TextProps & { typographyVariant?: TypographyProps['variant'] }) {
  // Determine which variant to use
  const resolvedVariant = typographyVariant || TEXT_VARIANT_MAP[variant]

  // Determine element - use provided 'as', or default element for the variant, or fallback
  const element =
    (as as string) ||
    DEFAULT_ELEMENTS[resolvedVariant] ||
    (variant === 'base' ? 'p' : 'span')

  // Get base styles
  let variantClasses = TYPOGRAPHY_STYLES[resolvedVariant]
  const colorClasses = color ? COLOR_STYLES[color] : ''

  // Add truncation classes if needed
  const truncateClasses = truncate
    ? 'truncate overflow-hidden whitespace-nowrap'
    : ''

  // If color is provided, remove default color from variant styles
  const baseClasses = color
    ? variantClasses.replace(/text-cencert-\w+/, '').trim()
    : variantClasses

  const combinedClassName = [
    baseClasses,
    colorClasses,
    truncateClasses,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return createElement(
    element as keyof JSX.IntrinsicElements,
    {
      className: combinedClassName,
      ...props,
    },
    children
  )
}

/**
 * Convenience components for common text elements
 */
export const Paragraph = (props: Omit<TextProps, 'as'>) =>
  createElement(Text, { as: 'p', ...props })

export const Span = (props: Omit<TextProps, 'as'>) =>
  createElement(Text, { as: 'span', ...props })

export const Strong = (props: Omit<TextProps, 'as'>) =>
  createElement(Text, { as: 'strong', ...props })

export const Emphasis = (props: Omit<TextProps, 'as'>) =>
  createElement(Text, { as: 'em', ...props })

export const Mark = (props: Omit<TextProps, 'as'>) =>
  createElement(Text, { as: 'mark', ...props })

/**
 * Small text component for captions, fine print, etc.
 */
export function Small({
  className = '',
  color,
  children,
  ...props
}: Omit<TextProps, 'variant' | 'as'>) {
  return createElement(Text, {
    variant: 'small',
    as: 'span',
    ...(color && { color }),
    className,
    children,
    ...props,
  })
}

/**
 * Typography compatibility component - forwards all props to Text with typographyVariant
 * This maintains backward compatibility with existing Typography usage
 */
export function Typography({
  variant,
  as,
  color,
  className = '',
  children,
  ...props
}: TypographyProps) {
  return createElement(Text, {
    typographyVariant: variant,
    as,
    color,
    className,
    children,
    ...props,
  })
}
